import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = {
  navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', error:'#EF4444',
  warning:'#F59E0B', border:'#E5E7EB', bg:'#F8FAFC', grey:'#6B7280',
  dark:'#1F2937', light:'#F9FAFB',
};

const ALL_PLANS = [
  'ai_basic','ai_ielts','ai_business',
  'online_group','online_standard','online_1on1','online_ielts',
  'starter_group','standard','premium','ielts_intensive','corporate',
];

const PLAN_LABELS = {
  ai_basic:'AI Practice Only', ai_ielts:'AI IELTS Prep', ai_business:'AI Business English',
  online_group:'Online Group Classes', online_standard:'Online Standard',
  online_1on1:'Online Premium Group', online_ielts:'Online IELTS Group',
  starter_group:'Group Classes', standard:'Standard', premium:'Premium Group',
  ielts_intensive:'IELTS Intensive', corporate:'Corporate Group',
};

const EMPTY_FORM = {
  name:'', description:'', discount_percent:'', promo_code:'',
  is_code_required: true, applies_to:'all', package_ids:[],
  valid_from: new Date().toISOString().split('T')[0],
  valid_until:'', max_uses:'',
};

function StatusBadge({ is_active, valid_until, times_used, max_uses }) {
  const now = new Date();
  const expired = valid_until && new Date(valid_until) < now;
  const exhausted = max_uses && times_used >= max_uses;
  if (!is_active) return <span style={{ background:'#FEE2E2', color:'#991B1B', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700 }}>Paused</span>;
  if (expired)    return <span style={{ background:'#FEF9C3', color:'#92400E', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700 }}>Expired</span>;
  if (exhausted)  return <span style={{ background:'#F3F4F6', color:'#6B7280', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700 }}>Used up</span>;
  return <span style={{ background:'#D1FAE5', color:'#065F46', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700 }}>Active</span>;
}

function F({ label, err, children, hint }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:'block', fontSize:13, fontWeight:600, color:C.dark, marginBottom:4 }}>{label}</label>
      {children}
      {hint && !err && <div style={{ fontSize:11, color:C.grey, marginTop:3 }}>{hint}</div>}
      {err  && <div style={{ fontSize:11, color:C.error, marginTop:3 }}>{err}</div>}
    </div>
  );
}

const inputStyle = (err) => ({
  width:'100%', padding:'9px 12px', border:`1.5px solid ${err ? C.error : C.border}`,
  borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box',
});

export default function AdminPromotions() {
  const [promos,    setPromos]    = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [errors,    setErrors]    = useState({});
  const [saving,    setSaving]    = useState(false);
  const [showForm,  setShowForm]  = useState(false);
  const [studentsModal, setStudentsModal] = useState(null); // {promo, list}
  const [loadingStudents, setLoadingStudents] = useState(false);

  const load = useCallback(() => {
    api.get('/api/admin/promotions').then(r => setPromos(r.data.promotions || [])).catch(() => {});
    api.get('/api/admin/promotions/analytics').then(r => setAnalytics(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';

  const validate = () => {
    const e = {};
    if (!form.name.trim())                      e.name = 'Name is required';
    const dp = parseFloat(form.discount_percent);
    if (isNaN(dp) || dp <= 0 || dp >= 100)      e.discount_percent = 'Discount must be 1%–99%';
    if (!form.valid_from)                        e.valid_from = 'Start date is required';
    if (form.valid_until && form.valid_from && form.valid_until <= form.valid_from)
                                                 e.valid_until = 'End date must be after start date';
    if (form.is_code_required && !form.promo_code.trim())
                                                 e.promo_code = 'Promo code is required when "require code" is on';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_percent: parseFloat(form.discount_percent),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        valid_until: form.valid_until || null,
        promo_code: form.is_code_required ? form.promo_code.toUpperCase().trim() : null,
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      };
      await api.post('/api/admin/promotions', payload);
      toast.success('Promotion created!');
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch(err) {
      const msg = err.response?.data?.error || 'Failed to create promotion';
      toast.error(msg);
      if (msg.includes('code')) setErrors(prev => ({ ...prev, promo_code: msg }));
    } finally { setSaving(false); }
  };

  const handlePause = async (id) => {
    try {
      const r = await api.post(`/api/admin/promotions/${id}/pause`);
      toast.success(r.data.message);
      load();
    } catch { toast.error('Failed to update promotion'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete promotion "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/promotions/${id}`);
      toast.success('Promotion deleted');
      load();
    } catch { toast.error('Failed to delete promotion'); }
  };

  const openStudents = async (promo) => {
    setLoadingStudents(true);
    setStudentsModal({ promo, list: [] });
    try {
      const r = await api.get(`/api/admin/promotions/${promo.id}/students`);
      setStudentsModal({ promo, list: r.data.students || [] });
    } catch { setStudentsModal({ promo, list: [] }); }
    setLoadingStudents(false);
  };

  return (
    <div style={{ padding:'28px 32px', maxWidth:1100, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
        <div>
          <h1 style={{ margin:0, fontWeight:800, fontSize:24, color:C.navy }}>Promotions & Discounts</h1>
          <p style={{ margin:'4px 0 0', color:C.grey, fontSize:14 }}>
            Create discount codes and automatic promotions for your students.
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          style={{ padding:'10px 22px', background:C.teal, color:'#fff', border:'none',
            borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
          {showForm ? '✕ Cancel' : '+ New Promotion'}
        </button>
      </div>

      {/* Analytics strip */}
      {analytics && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14, marginBottom:28 }}>
          {[
            { label:'Students this month via promos', value: analytics.students_acquired_this_month, icon:'👥' },
            { label:'Total discount given (all time)', value:`$${parseFloat(analytics.total_discount_given || 0).toFixed(2)}`, icon:'💸' },
            { label:'Best performing promotion', value: analytics.best_promotion?.name || '—', icon:'🏆',
              sub: analytics.best_promotion ? `${analytics.best_promotion.uses} uses` : '' },
          ].map(card => (
            <div key={card.label} style={{ background:'#fff', borderRadius:12, padding:'16px 20px',
              border:`1px solid ${C.border}`, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize:22, marginBottom:6 }}>{card.icon}</div>
              <div style={{ fontWeight:800, fontSize:20, color:C.navy }}>{card.value}</div>
              {card.sub && <div style={{ fontSize:12, color:C.teal, fontWeight:600 }}>{card.sub}</div>}
              <div style={{ fontSize:11, color:C.grey, marginTop:2 }}>{card.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <div style={{ background:'#fff', borderRadius:16, padding:'28px 32px', marginBottom:28,
          border:`1px solid ${C.border}`, boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
          <h3 style={{ margin:'0 0 24px', fontWeight:800, fontSize:17, color:C.navy }}>Create New Promotion</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              <F label="Promotion Name *" err={errors.name}
                hint="Shown to students, e.g. 'Spring Launch Offer'">
                <input value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))}
                  placeholder="Spring Launch Offer" style={inputStyle(errors.name)} />
              </F>
              <F label="Internal Note" hint="Not shown to students — for your reference only">
                <input value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))}
                  placeholder="Optional internal note" style={inputStyle()} />
              </F>
              <F label="Discount Percentage *" err={errors.discount_percent}>
                <div style={{ position:'relative' }}>
                  <input type="number" min="1" max="99" value={form.discount_percent}
                    onChange={e => setForm(p=>({...p,discount_percent:e.target.value}))}
                    placeholder="e.g. 20" style={{...inputStyle(errors.discount_percent), paddingRight:32}} />
                  <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                    fontSize:13, color:C.grey, pointerEvents:'none' }}>%</span>
                </div>
              </F>

              <F label="Applies To">
                <select value={form.applies_to} onChange={e => setForm(p=>({...p,applies_to:e.target.value}))}
                  style={inputStyle()}>
                  <option value="all">All packages</option>
                  <option value="specific">Specific packages</option>
                  <option value="extra_sessions">Extra Sessions only</option>
                </select>
              </F>

              {form.applies_to === 'specific' && (
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={{ fontSize:13, fontWeight:600, color:C.dark, display:'block', marginBottom:8 }}>
                    Select packages
                  </label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {ALL_PLANS.map(pid => (
                      <label key={pid} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer',
                        background: form.package_ids.includes(pid) ? C.teal+'18' : C.light,
                        border:`1.5px solid ${form.package_ids.includes(pid) ? C.teal : C.border}`,
                        borderRadius:8, padding:'5px 12px', fontSize:12, fontWeight:600,
                        color: form.package_ids.includes(pid) ? C.teal : C.grey }}>
                        <input type="checkbox" checked={form.package_ids.includes(pid)}
                          onChange={e => setForm(p => ({
                            ...p, package_ids: e.target.checked
                              ? [...p.package_ids, pid]
                              : p.package_ids.filter(x => x !== pid)
                          }))} style={{ display:'none' }} />
                        {PLAN_LABELS[pid] || pid}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Require promo code toggle */}
              <div style={{ gridColumn:'1/-1', background:C.light, borderRadius:12, padding:'16px 20px', border:`1px solid ${C.border}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: form.is_code_required ? 16 : 0 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:C.dark }}>Require promo code</div>
                    <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>
                      {form.is_code_required
                        ? 'Student must enter a code to get the discount'
                        : 'Discount applies automatically to all eligible students — no code needed'}
                    </div>
                  </div>
                  <button type="button" onClick={() => setForm(p=>({...p,is_code_required:!p.is_code_required}))}
                    style={{ width:48, height:26, borderRadius:13, border:'none', cursor:'pointer',
                      background: form.is_code_required ? C.teal : '#D1D5DB', position:'relative', transition:'background 0.2s' }}>
                    <span style={{ position:'absolute', top:3, left: form.is_code_required ? 25 : 3,
                      width:20, height:20, background:'#fff', borderRadius:10, transition:'left 0.2s' }} />
                  </button>
                </div>
                {form.is_code_required && (
                  <F label="Promo Code *" err={errors.promo_code}
                    hint="e.g. SPRING2026 — students enter this at registration">
                    <input value={form.promo_code} onChange={e => setForm(p=>({...p,promo_code:e.target.value.toUpperCase()}))}
                      placeholder="SPRING2026" style={inputStyle(errors.promo_code)} />
                  </F>
                )}
              </div>

              <F label="Valid From *" err={errors.valid_from}>
                <input type="date" value={form.valid_from}
                  onChange={e => setForm(p=>({...p,valid_from:e.target.value}))}
                  style={inputStyle(errors.valid_from)} />
              </F>
              <F label="Valid Until" hint="Leave blank for no end date">
                <input type="date" value={form.valid_until}
                  onChange={e => setForm(p=>({...p,valid_until:e.target.value}))}
                  style={inputStyle(errors.valid_until)} />
                {errors.valid_until && <div style={{ fontSize:11, color:C.error, marginTop:3 }}>{errors.valid_until}</div>}
              </F>
              <F label="Maximum Uses" hint="Leave blank for unlimited">
                <input type="number" min="1" value={form.max_uses}
                  onChange={e => setForm(p=>({...p,max_uses:e.target.value}))}
                  placeholder="Unlimited" style={inputStyle()} />
              </F>
            </div>

            <div style={{ display:'flex', gap:12, marginTop:8 }}>
              <button type="submit" disabled={saving}
                style={{ padding:'11px 28px', background:C.teal, color:'#fff', border:'none',
                  borderRadius:10, fontWeight:700, fontSize:14, cursor:saving?'not-allowed':'pointer',
                  opacity:saving?0.7:1 }}>
                {saving ? 'Creating…' : 'Create Promotion'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setErrors({}); }}
                style={{ padding:'11px 20px', background:C.light, color:C.grey, border:`1px solid ${C.border}`,
                  borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Active promotions table */}
      <div style={{ background:'#fff', borderRadius:16, border:`1px solid ${C.border}`,
        boxShadow:'0 1px 4px rgba(0,0,0,0.05)', overflow:'hidden' }}>
        <div style={{ padding:'18px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontWeight:700, fontSize:16, color:C.navy }}>
            All Promotions {promos.length > 0 && <span style={{ color:C.grey, fontWeight:500 }}>({promos.length})</span>}
          </h3>
        </div>

        {promos.length === 0 ? (
          <div style={{ padding:48, textAlign:'center', color:C.grey }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🏷️</div>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:6, color:C.dark }}>No promotions yet</div>
            <div style={{ fontSize:14 }}>Create your first promotion to start attracting students with discounts.</div>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:C.bg }}>
                  {['Name','Discount','Code','Applies To','Valid Until','Uses','Status','Actions'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:12,
                      fontWeight:700, color:C.grey, borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {promos.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom:`1px solid ${C.border}`,
                    background: i % 2 === 0 ? '#fff' : C.bg }}>
                    <td style={{ padding:'12px 16px', fontSize:14, fontWeight:600, color:C.dark }}>
                      {p.name}
                      {p.description && <div style={{ fontSize:11, color:C.grey, fontWeight:400, marginTop:2 }}>{p.description}</div>}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:14, fontWeight:700, color:C.success }}>
                      {p.discount_percent}%
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13 }}>
                      {p.is_code_required ? (
                        <span style={{ background:'#EFF6FF', color:'#1E40AF', padding:'2px 8px',
                          borderRadius:6, fontSize:12, fontWeight:700, fontFamily:'monospace' }}>
                          {p.promo_code}
                        </span>
                      ) : (
                        <span style={{ color:C.grey, fontSize:12 }}>Auto (no code)</span>
                      )}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, color:C.grey }}>
                      {p.applies_to === 'all' ? 'All packages'
                        : p.applies_to === 'extra_sessions' ? 'Extra Sessions'
                        : `${(p.package_ids||[]).length} package${(p.package_ids||[]).length !== 1 ? 's' : ''}`}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, color:C.grey }}>
                      {fmtDate(p.valid_until)}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13 }}>
                      <button onClick={() => openStudents(p)}
                        style={{ background:'none', border:'none', cursor:'pointer', color:C.teal,
                          fontWeight:600, fontSize:13, textDecoration:'underline', padding:0 }}>
                        {p.times_used}{p.max_uses ? `/${p.max_uses}` : ''} uses
                      </button>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <StatusBadge {...p} />
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => handlePause(p.id)}
                          style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${C.border}`,
                            background:'#fff', fontSize:12, cursor:'pointer', fontWeight:600, color:C.grey }}>
                          {p.is_active ? 'Pause' : 'Resume'}
                        </button>
                        <button onClick={() => handleDelete(p.id, p.name)}
                          style={{ padding:'4px 10px', borderRadius:6, border:`1px solid #FCA5A5`,
                            background:'#FEF2F2', fontSize:12, cursor:'pointer', fontWeight:600, color:'#DC2626' }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Per-promotion analytics */}
      {analytics?.per_promotion?.length > 0 && (
        <div style={{ background:'#fff', borderRadius:16, border:`1px solid ${C.border}`, marginTop:20,
          boxShadow:'0 1px 4px rgba(0,0,0,0.05)', overflow:'hidden' }}>
          <div style={{ padding:'16px 24px', borderBottom:`1px solid ${C.border}` }}>
            <h3 style={{ margin:0, fontWeight:700, fontSize:15, color:C.navy }}>Promotion Performance</h3>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:C.bg }}>
                {['Promotion','Discount','Total Uses','Discount Given'].map(h => (
                  <th key={h} style={{ padding:'9px 16px', textAlign:'left', fontSize:12,
                    fontWeight:700, color:C.grey, borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {analytics.per_promotion.map((p, i) => (
                <tr key={p.id} style={{ borderBottom:`1px solid ${C.border}`,
                  background: i % 2 === 0 ? '#fff' : C.bg }}>
                  <td style={{ padding:'10px 16px', fontSize:14, fontWeight:600 }}>{p.name}</td>
                  <td style={{ padding:'10px 16px', fontSize:14, color:C.success, fontWeight:700 }}>{p.discount_percent}%</td>
                  <td style={{ padding:'10px 16px', fontSize:14 }}>
                    <button onClick={() => openStudents({ id: p.id, name: p.name })}
                      style={{ background:'none', border:'none', cursor:'pointer', color:C.teal,
                        fontWeight:600, fontSize:14, textDecoration:'underline', padding:0 }}>
                      {p.uses}
                    </button>
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:14, color:C.error, fontWeight:700 }}>
                    -${p.total_discount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Students who used this promo — modal */}
      {studentsModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000,
          display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={e => { if (e.target === e.currentTarget) setStudentsModal(null); }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:600,
            maxHeight:'80vh', overflow:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, fontWeight:800, fontSize:17 }}>
                Students — {studentsModal.promo.name}
              </h3>
              <button onClick={() => setStudentsModal(null)}
                style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.grey }}>✕</button>
            </div>
            {loadingStudents ? (
              <div style={{ textAlign:'center', padding:30, color:C.grey }}>Loading…</div>
            ) : studentsModal.list.length === 0 ? (
              <div style={{ textAlign:'center', padding:30, color:C.grey }}>No students have used this promotion yet.</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:C.bg }}>
                    {['Student','Package','Original','Discount','Paid','Date'].map(h => (
                      <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700,
                        color:C.grey, borderBottom:`1px solid ${C.border}`, fontSize:12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {studentsModal.list.map((s, i) => (
                    <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                      <td style={{ padding:'9px 12px', fontWeight:600 }}>{s.name}</td>
                      <td style={{ padding:'9px 12px', color:C.grey }}>{s.plan_type || '—'}</td>
                      <td style={{ padding:'9px 12px' }}>${parseFloat(s.original_price||0).toFixed(2)}</td>
                      <td style={{ padding:'9px 12px', color:C.error, fontWeight:700 }}>-${parseFloat(s.discount_amount||0).toFixed(2)}</td>
                      <td style={{ padding:'9px 12px', color:C.success, fontWeight:700 }}>${parseFloat(s.final_price||0).toFixed(2)}</td>
                      <td style={{ padding:'9px 12px', color:C.grey }}>
                        {s.applied_at ? new Date(s.applied_at).toLocaleDateString('en-GB') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
